import { ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import { toPersianDigit } from '@/helpers/utils';
import type {
  GeoAnalysisData,
  GeoDimensionItem,
  GeoDimensionKey,
  SeoPriorityTone
} from '@/app/ContentTools/types/aiContentAnalysisApiTypes';

const DIMENSION_ICONS: Record<GeoDimensionKey, IconProp> = {
  citability: 'file-alt',
  structural: 'project-diagram',
  evidence: 'book',
  authority: 'user-shield',
  technical: 'cog'
};

const DIMENSION_HELP: Record<GeoDimensionKey, string> = {
  citability:
    'تعاریف صریح، پاسخ‌های مستقل و ساختار قابل استخراج برای مدل‌های جستجوی مولد.',
  structural:
    'وضوح پاسخ، عناوین منظم و قطعه‌بندی محتوا برای درک بهتر توسط هوش مصنوعی.',
  evidence: 'کیفیت شواهد، منابع، آمار و استنادهای قابل اتکا در متن صفحه.',
  authority: 'هویت نویسنده، زمینه اعتبار برند و سیگنال‌های اعتماد صفحه.',
  technical:
    'دسترسی فنی ربات‌ها به متن، اسکیما، متادیتا و ساختار HTML قابل خواندن.'
};

const progressVariant = (tone: SeoPriorityTone) => {
  if (tone === 'success') return 'success';
  if (tone === 'info') return 'info';
  if (tone === 'warning') return 'warning';
  return 'danger';
};

const GeoDimensionRow = ({ item }: { item: GeoDimensionItem }) => (
  <div className="d-flex align-items-center gap-2 geo-dimension-row">
    <span
      className={`d-flex align-items-center justify-content-center rounded-circle bg-soft-${item.tone} text-${item.tone} flex-shrink-0 fs--2 geo-dimension-row__icon`}
    >
      <FontAwesomeIcon icon={DIMENSION_ICONS[item.key]} />
    </span>
    <span className="d-inline-flex align-items-center min-w-0 flex-grow-1 fw-semi-bold fs--2 text-700">
      <span className="text-truncate">{item.label}</span>
      <HelpTooltip placement="top" html={DIMENSION_HELP[item.key]} />
    </span>
    <ProgressBar
      now={Math.min(Math.max(item.score, 0), 100)}
      variant={progressVariant(item.tone)}
      className="geo-dimension-row__bar bg-200"
    />
    <span
      className={`flex-shrink-0 fs--2 fw-bold lh-1 text-center text-${item.tone}`}
      style={{ minWidth: '1.5rem' }}
    >
      {toPersianDigit(`${item.score}`)}
    </span>
  </div>
);

export const GeoDimensionsOverview = ({
  dimensions
}: {
  dimensions: GeoAnalysisData['dimensions'];
}) => (
  <div className="d-flex flex-column justify-content-evenly flex-grow-1 w-100">
    {dimensions.map(item => (
      <GeoDimensionRow key={item.key} item={item} />
    ))}
  </div>
);

export const GeoDimensionsOverviewPending = () => (
  <div className="d-flex flex-column justify-content-evenly flex-grow-1 w-100">
    {Array.from({ length: 5 }, (_, index) => (
      <div
        key={index}
        className="d-flex align-items-center gap-2 geo-dimension-row"
      >
        <TextLoading width={22} height={22} isWidthPx />
        <TextLoading width={42} height={8} />
        <div className="geo-dimension-row__bar">
          <TextLoading width={100} height={4} />
        </div>
        <TextLoading width={18} height={10} isWidthPx />
      </div>
    ))}
  </div>
);
